import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ClipboardList } from "lucide-react";
import { api } from "../../lib/api";
import { RATING_META, activeSkills, ratingsMap } from "../../lib/inventorySkills";
import { PLAY_TYPES, usePlayType } from "../../lib/playTypes";
import { useFinishingLibrary } from "../../lib/finishing";
import { useShootingSection } from "../../lib/shooting";
import { useBallHandling } from "../../lib/ballHandling";
import { useDefense } from "../../lib/defense";
import { useRebounding } from "../../lib/rebounding";
import { FinishingPointsTo } from "../inventory/finishing/FinishingPointsTo";
import { ShootingPointsTo } from "../inventory/shooting/ShootingPointsTo";
import { EMPTY_SHOOTING } from "../inventory/shooting/ShootingCategory";
import { BallHandlingPointsTo } from "../inventory/ballhandling/BallHandlingPointsTo";
import { EMPTY_BH } from "../inventory/ballhandling/BallHandlingCategory";
import { DefensePointsTo } from "../inventory/defense/DefensePointsTo";
import { EMPTY_DEF } from "../inventory/defense/DefenseCategory";
import { ReboundingPointsTo } from "../inventory/rebounding/ReboundingPointsTo";
import { EMPTY_RB } from "../inventory/rebounding/ReboundingCategory";
import { PlayTypePointsTo } from "../inventory/playtype/PlayTypePointsTo";
import { EMPTY_PT } from "../inventory/playtype/PlayTypeCategory";

const EMPTY_FIN = { outcome_tags: [], finish_types: {}, feel: [], mindset: [], limiting_factors: [], base_finish_strengths: [] };
const LANES = [["limiter", "Rate Limiters · improve"], ["good", "Good → Great"], ["anchor", "Anchors · keep building on"], ["unrated", "Not rated yet"]];
const RANK = { limiter: 0, good: 1, anchor: 2 };

// The Plan: every section's Panel 4 (moved from the Development Inventory). One card per section with tags, grouped by the section's worst interaction rating.
const TECH = [
  { key: "finishing", category: "Finishing", use: useFinishingLibrary, empty: EMPTY_FIN, render: (lib, b, p) => <FinishingPointsTo lib={lib} fin={b} {...p} /> },
  { key: "shooting", category: "Shooting", use: useShootingSection, empty: EMPTY_SHOOTING, render: (lib, b, p) => <ShootingPointsTo lib={lib} sh={b} {...p} /> },
  { key: "ball_handling", category: "Ball Handling / Passing", use: useBallHandling, empty: EMPTY_BH, render: (lib, b, p, doc) => <BallHandlingPointsTo lib={lib} bh={b} finishing={doc.finishing} {...p} /> },
  { key: "defense", category: "Defense", use: useDefense, empty: EMPTY_DEF, render: (lib, b, p) => <DefensePointsTo lib={lib} df={b} {...p} /> },
  { key: "rebounding", category: "Rebounding", use: useRebounding, empty: EMPTY_RB, render: (lib, b, p) => <ReboundingPointsTo lib={lib} rb={b} {...p} /> },
];
export const PLAN_SECTIONS = [...TECH, ...Object.entries(PLAY_TYPES).map(([category, cfg]) => ({ key: cfg.key, category, cfg, empty: EMPTY_PT }))];

export const hasTags = (block) => (block?.outcome_tags || []).length > 0;
// Lane = worst rating among the section's interactions (the active skills in that category): Rate Limiter > Good > Anchor; none rated → "unrated".
export const sectionLane = (sec, byId) => {
  const r = activeSkills().filter(s => s.category === sec.category).map(s => byId[s.id]?.rating).filter(x => x in RANK).sort((a, b) => RANK[a] - RANK[b]);
  return r[0] || "unrated";
};

function SectionCard({ sec, lib, doc, playerId, canEdit, onDoc }) {
  const block = doc[sec.key] || sec.empty;
  const byId = ratingsMap(doc.ratings);
  const meta = RATING_META[sectionLane(sec, byId)];
  const onChange = async (fn) => {
    const next = typeof fn === "function" ? fn(doc[sec.key] || sec.empty) : fn;
    onDoc({ ...doc, [sec.key]: next });
    try { onDoc(await api.updateDevAssessment(playerId, doc.id, { [sec.key]: next })); } catch { toast.error("Could not save the plan"); }
  };
  const p = { readOnly: !canEdit, onChange };
  return (
    <div id={`plan-${sec.key}`} className="border bg-[#101010] p-3 space-y-2 scroll-mt-40" style={{ borderColor: meta ? `${meta.color}55` : "rgba(255,255,255,.1)" }} data-testid={`plan-section-${sec.key}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="text-[10px] uppercase tracking-[0.35em] font-bold text-white">{sec.category}</div>
        {meta && <span className="text-[8px] uppercase tracking-[0.2em] px-1.5 py-0.5 border" style={{ color: meta.color, borderColor: `${meta.color}66` }} data-testid={`plan-section-${sec.key}-lane`}>{meta.label}</span>}
        <span className="text-[10px] text-[#757575]">{(block.outcome_tags || []).length} tagged · {(block.outcome_tags || []).filter(t => t.most_seen).length} starred</span>
        <Link to={`/player/${playerId}?tab=inventory`} className="ml-auto text-[9px] uppercase tracking-[0.25em] text-[#a0a0a0] hover:text-white" data-testid={`plan-section-${sec.key}-diagnosis`}>Diagnosis → Inventory</Link>
      </div>
      {sec.cfg
        ? <PlayTypePointsTo cfg={sec.cfg} lib={lib} pt={block} byId={byId} playerId={playerId} role={sec.cfg.roleField ? doc[sec.cfg.roleField] || null : undefined} {...p} />
        : sec.render(lib, block, p, doc)}
    </div>
  );
}

// Each section loads its own library (the same hooks the Inventory uses) and renders once it's there.
function Section({ sec, ...rest }) {
  const techLib = sec.use ? sec.use() : null;   // eslint-disable-line react-hooks/rules-of-hooks
  const ptLib = usePlayType(sec.cfg ? sec.cfg.key : null);
  const lib = sec.cfg ? ptLib : techLib;
  if (!lib) return <div className="text-[10px] text-[#757575]" data-testid={`plan-section-${sec.key}-loading`}>Loading {sec.category}…</div>;
  return <SectionCard sec={sec} lib={lib} {...rest} />;
}

export default function ThePlan({ playerId, doc, docs, onSelect, onDoc, canEdit }) {
  useEffect(() => {
    const id = (window.location.hash || "").slice(1);
    if (!id || !doc) return;
    const t = setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }), 600);
    return () => clearTimeout(t);
  }, [doc?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const byId = ratingsMap(doc?.ratings);
  const tagged = doc ? PLAN_SECTIONS.filter(s => hasTags(doc[s.key])) : [];
  return (
    <section className="space-y-3" data-testid="the-plan">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.35em] font-bold text-[var(--org-accent)]"><ClipboardList className="w-3.5 h-3.5" /> The Plan</div>
          <div className="text-[10px] text-[#757575] mt-0.5">What to work on and how · each section's plan from the tags in the Development Inventory · Rate Limiters first</div>
        </div>
        {docs?.length > 1 && (
          <select value={doc?.id || ""} onChange={e => onSelect(docs.find(a => a.id === e.target.value))} className="bg-[#0f0f0f] border border-white/10 px-3 py-2 text-sm text-white" data-testid="plan-assessment-select">
            {docs.map(a => <option key={a.id} value={a.id}>{(a.created_at || "").slice(0, 10)} · {a.label || "Assessment"} · {a.status === "final" ? "Final" : "Draft"}</option>)}
          </select>
        )}
      </div>
      {!doc ? <div className="border border-dashed border-white/15 p-6 text-sm text-[#a0a0a0]" data-testid="plan-empty">No assessment yet. The plan builds from the tags in the Development Inventory.</div>
        : tagged.length === 0 ? <div className="border border-dashed border-white/15 p-6 text-sm text-[#a0a0a0]" data-testid="plan-empty">Nothing tagged yet. Tag where it breaks down in the Development Inventory (panel 3) and the plan shows here.</div>
        : LANES.map(([lane, title]) => {
          const secs = tagged.filter(s => sectionLane(s, byId) === lane);
          if (!secs.length) return null;
          const meta = RATING_META[lane];
          return (
            <div key={lane} className="space-y-2" data-testid={`plan-lane-${lane}`}>
              <div className="flex items-center gap-2 border-b border-white/10 pb-1.5"><div className="text-[10px] uppercase tracking-[0.3em] font-bold" style={{ color: meta?.color || "#a0a0a0" }}>{title}</div><div className="text-[9px] uppercase tracking-widest text-[#757575]">{secs.length} {secs.length === 1 ? "section" : "sections"}</div></div>
              {secs.map(sec => <Section key={sec.key} sec={sec} doc={doc} playerId={playerId} canEdit={canEdit} onDoc={onDoc} />)}
            </div>
          );
        })}
    </section>
  );
}
